import React, { useEffect, useState } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import {
  ALERT_MESSAGES,
  ALERT_TITLES,
  OnBoardingStepProps,
} from "../../../constants";
import { alertMessage } from "../../../services/alertService";
import {
  AgreementContainer,
  Container,
  ErrorMessage,
  Paragraph,
  Section,
  Subtitle,
  TermsBox,
  Title,
} from "../../../styles/terms&conditions";

import {
  FormContainer,
} from "../../../styles/PersonalInfoStep";
import { POST } from "../../../services/HTTPServices";
import { useNavigate } from "react-router-dom";
import { setItem, setItemWithObject } from "../../../utils/cache/LocalStorage";
import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../../styles/loading";
import { StepForm } from "../../../components/signup/StepForm";

const TermsAndConditionsStep: React.FC<OnBoardingStepProps> = ({
  formData,
  onChange,
  onNext,
  onBack,
  onAssign,
  changedRole,
}) => {
  const navigate = useNavigate();

  const [agreed, setAgreed] = useState<boolean>(formData.termsAgreed || false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const [loading, setLoading] = React.useState(false);
  const [authUser, setAuthUser] = React.useState({
    name: "",
    id: -1,
    email: "",
    mobile: "",
    password: "",
    city: "",
  });

  useEffect(() => {
    const tempUser = JSON.parse(localStorage.getItem("tempUser") || "null");
    if (tempUser) {
      setAuthUser(tempUser);
      onAssign(tempUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAgreementChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAgreed(event.target.checked);
    onChange("termsAgreed", event.target.checked);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (agreed) {
      signupFirstStep();
    } else {
      alertMessage(ALERT_TITLES.WARNING, ALERT_MESSAGES.AGREE_TA);
    }
  };

  const signupFirstStep = async () => {
    setLoading(true);

    let url = changedRole
      ? "serviceprovider/UserToMerchantSignUp"
      : "serviceprovider/MerchantSignUp";

    let temp = changedRole
      ? {
          name: authUser?.name,
          email: authUser?.email,
          mobile: "+65" + authUser?.mobile,
          city: authUser?.city,
        }
      : {
          name: authUser?.name,
          email: authUser?.email,
          mobile: "+65" + authUser?.mobile,
          password: authUser?.password,
          city: authUser?.city,
        };

    let res: any = await POST(url, temp, "provider");

    if (res && res.status === 200) {
      setLoading(false);
      setItemWithObject("tempUser", formData);
      setItem("provider_login_email", formData.email);
      setItem("provider_login_mobile", formData.mobile);

      onNext();
    } else {
      if (res.status && res.status === 401) navigate("/unauthorized-page");
      else {
        alertMessage(ALERT_TITLES.ERROR, res.message);
      }

      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}

      <FormContainer>
        <StepForm onNext={handleSubmit} onBack={onBack}>
          <Container>
            <Paragraph className="mb-32 text-lg font-normal">
              Please read the Terms & Conditions
            </Paragraph>

            <TermsBox>
              <div>
                <Title>Terms and Conditions</Title>
                <Paragraph>Effective date: October 24, 2024</Paragraph>

                <Section>
                  <Subtitle>Personal Data</Subtitle>
                  <Paragraph>
                    Welcome to Send Application, this Data Protection Notice
                    ("Notice") sets out the basis which Send Services Pte. Ltd.
                    ("we", "us", or "our") may collect, use, disclose or
                    otherwise process personal data of our customers in
                    accordance with the Personal Data Protection Act ("PDPA").
                    This Notice applies to personal data in our possession or
                    under our control, including personal data in the possession
                    of organisations which we have engaged to collect, use,
                    disclose or process personal data for our purposes.
                  </Paragraph>
                  <br />
                  <Paragraph as="div">
                    1. &nbsp;&nbsp;&nbsp; As used in this Notice:
                    <Paragraph as="div" className="ml-24">
                      "<strong>customer</strong>" means an individual who (a)
                      has contacted us through any means to find out more about
                      any goods or services we provide, or (b) may, or has,
                      entered into a contract with us for the supply of any
                      goods or services by us; and
                    </Paragraph>
                    <Paragraph as="div" className="ml-24">
                      "<strong>personal data</strong>" means data, whether true
                      or not, about a customer who can be identified: (a) from
                      that data; or (b) from that data and other information to
                      which we have or are likely to have access.
                    </Paragraph>
                  </Paragraph>
                  <Paragraph>
                    2. &nbsp;&nbsp;&nbsp; Depending on the nature of your
                    interaction with us, some examples of personal data which we
                    may collect from you include name, residential address,
                    email address, telephone number, nationality, gender, date
                    of birth, marital status, photograph, employment information
                    and financial information.
                  </Paragraph>
                  <Paragraph>
                    3. &nbsp;&nbsp;&nbsp; Other terms used in this Notice shall
                    have the meanings given to them in the PDPA (where the
                    context so permits).
                  </Paragraph>
                </Section>

                <Section>
                  <Subtitle>
                    Collection, Use, and Disclosure of Personal Data
                  </Subtitle>
                  <Paragraph>
                    4. &nbsp;&nbsp;&nbsp; We generally do not collect your
                    personal data unless (a) it is provided to us voluntarily by
                    you directly or via a third party who has been duly
                    authorised by you to disclose your personal data to us (your
                    "authorised representative") after (i) you (or your
                    authorised representative) have been notified of the
                    purposes for which the data is collected, and (ii) you (or
                    your authorised representative) have provided written
                    consent to the collection and usage of your personal data
                    for those purposes, or (b) collection and use of personal
                    data without consent is permitted or required by the PDPA or
                    other laws. We shall seek your consent before collecting any
                    additional personal data and before using your personal data
                    for a purpose which has not been notified to you (except
                    where permitted or authorised by law).
                  </Paragraph>
                </Section>
              </div>
            </TermsBox>
            <AgreementContainer
>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={handleAgreementChange}
                    className="green-checkbox ml-negative-16"
                  />
                }
                label="I agree to the terms and conditions."
              />
              {!agreed && submitAttempted && (
                <ErrorMessage>
                  You must agree to the Terms & Conditions to proceed.
                </ErrorMessage>
              )}
            </AgreementContainer>
          </Container>
        </StepForm>
      </FormContainer>
    </>
  );
};

export default TermsAndConditionsStep;
